// ─── What: shared BUY YES / SELL NO trade plate label ─────────────────────────
//
// One source of truth for the trade "plate" text shown both in the trades list
// (TradeRow) and the in-plot hover tooltip, so they stay consistent. Both colour
// the plate by side (buy = green, sell = red).

import {
    outcomeLabel,
    OUTCOME_MAXLEN_BADGE,
    type OutcomeLabel,
} from '../../utils/outcomeLabels';

/** "BUY KACPER ADAMUS" / "SELL NO" / "BUY" (when the outcome is unknown). */
export function plateLabel(
    side: 'buy' | 'sell',
    outcomeIndex: number | null,
    outcomes?: string[] | null,
    maxLen: number = OUTCOME_MAXLEN_BADGE
): OutcomeLabel {
    const action = side === 'buy' ? 'BUY' : 'SELL';
    if (outcomeIndex === null) return { text: action, title: action };
    const named = outcomeLabel(outcomes, outcomeIndex, maxLen);
    if (named)
        return {
            text: `${action} ${named.text}`,
            title: `${action} ${named.title}`,
        };
    const legacy = `${action} ${outcomeIndex === 0 ? 'YES' : 'NO'}`;
    return { text: legacy, title: legacy };
}

// ─── Shared trade identity (for list <-> plot hover linking) ───────────────────

export interface TradeKeyParts {
    account: string;
    marketId: string;
    timestamp: Date;
    side: 'buy' | 'sell';
    outcomeIndex: number | null;
    price: number;
    amount: number;
    quoteAmount: number;
}

/**
 * Stable key identifying a trade across the list and the plot overlay (which
 * derive from the same source but are different object shapes — no shared id).
 * amount/quoteAmount are included to make same-ms/same-price fills from one
 * account distinguishable; a residual collision only highlights both, which is
 * cosmetic.
 */
export function tradeKey(t: TradeKeyParts): string {
    return [
        t.account,
        t.marketId,
        t.timestamp.getTime(),
        t.side,
        t.outcomeIndex,
        t.price,
        t.amount,
        t.quoteAmount,
    ].join('|');
}
